import { twMerge } from "tailwind-merge";
import { FeatureAnnouncements } from "~/shared/components/FeatureAnnoucements/FeatureAnnouncements";
import { NavMenu } from "~/shared/components/NavigationMenu";
import { PageActionsPortalProvider } from "~/shared/components/PageLayout";
import { SidebarNav } from "~/shared/components/PrivateAppLayout/components/SidebarNav";
import { CommandMenuProvider } from "~/shared/components/PrivateAppLayout/components/SiteHeader/CommandMenu";
import {
  MobileNavMenuPortalContainer,
  MobileNavMenuPortalProvider,
} from "~/shared/components/PrivateAppLayout/components/SiteHeader/MobileNav";
import {
  SiteHeader,
  SiteHeaderPortalProvider,
} from "~/shared/components/PrivateAppLayout/components/SiteHeader/SiteHeader";
import { SidebarNavProvider, useSidebarNavState } from "~/shared/components/PrivateAppLayout/context/SidebarNavContext";
import { useReactQuerySubscription } from "~/shared/hooks/useReactQuerySubscription";
import { useElementStore } from "~/shared/stores/element-store";
import { ProductTeamProductionWarning } from "~/team/components/ProductTeamProductionWarning";
import { Impersonation, useImpersonationState } from "./components/Impersonation";

interface PrivateAppLayoutProps {
  children: React.ReactNode;
}
export function PrivateAppLayout({ children }: PrivateAppLayoutProps) {
  useReactQuerySubscription();

  return (
    <CommandMenuProvider>
      <MobileNavMenuPortalProvider>
        <SidebarNavProvider>
          <SiteHeaderPortalProvider>
            <PageActionsPortalProvider>
              <Layout>{children}</Layout>
            </PageActionsPortalProvider>
          </SiteHeaderPortalProvider>
        </SidebarNavProvider>
      </MobileNavMenuPortalProvider>
    </CommandMenuProvider>
  );
}

interface LayoutProps {
  children: React.ReactNode;
}
function Layout({ children }: LayoutProps) {
  const { isOpen } = useSidebarNavState() ?? {};
  const { value: impersonation } = useImpersonationState();
  const setElement = useElementStore((state) => state.setElement);

  return (
    <div
      data-impersonation={impersonation ? "true" : "false"}
      data-sidebar-state={isOpen ? "open" : "closed"}
      className={twMerge(
        "[--siteheader-position-y:0px] data-[impersonation=true]:[--siteheader-position-y:44px]",
        "[--siteheader-height:58px] md:[--siteheader-height:71px]",
        // The sidebar width is set to `0px` on mobile, and `81px` when closed and `268px` when open on tablet and larger screens.
        "[--sidebar-width:0px] md:data-[sidebar-state=closed]:[--sidebar-width:81px] md:data-[sidebar-state=open]:[--sidebar-width:268px]",
        "bg-ds-surface-foundation relative h-full min-h-dvh",
      )}
    >
      <aside className="pointer-events-auto inset-0 z-30 hidden w-(--sidebar-width) md:fixed md:flex">
        <SidebarNav>
          <NavMenu isOpen={isOpen} />
        </SidebarNav>
      </aside>
      <Impersonation />
      <div className="sticky top-(--siteheader-position-y) left-0 z-20 h-(--siteheader-height) w-[calc(100%-var(--sidebar-width))] translate-x-(--sidebar-width)">
        <SiteHeader />
      </div>

      <div className="pointer-events-none fixed top-(--siteheader-height) left-0 z-20 h-full min-h-[calc(100dvh-var(--siteheader-height))] w-full md:hidden">
        {/** Mobile Nav Menu will be injected into the DOM here */}
        <MobileNavMenuPortalContainer />
      </div>
      <main className="text-ds-text-primary relative z-10 w-full pl-(--sidebar-width)">{children}</main>
      <div
        className="pointer-events-none fixed inset-0 top-[calc(var(--siteheader-height)+var(--siteheader-position-y))] z-10 w-[calc(100%-var(--sidebar-width))] translate-x-(--sidebar-width)"
        ref={(node) => {
          setElement({ name: "layoutSheetRef", element: node });
        }}
      />
      <FeatureAnnouncements />
      <ProductTeamProductionWarning />
    </div>
  );
}
